/**
 * New calendar / "Name and colour" of a calendar in the rail. Additional own calendars are folders of the mailbox
 * (name visible in Outlook and on phones); the colour is a per-user setting of the webmail. The default calendar and
 * added calendars only change their colour.
 */
import { useI18n } from '@platform/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, toast, useErrorText } from '@platform/ui';
import { useId, useState, type FormEvent } from 'react';
import { api, type CalendarInfo } from '../api';
import { isExtraCalendar } from '../actions/calendar-actions';
import { useCalendar } from './calendar-context';
import { ColorChoice } from './color-choice';
import { calendarName } from './events';

type Color = CalendarInfo['color'];

export function CalendarNameDialog({ calendar, onClose }: { calendar: CalendarInfo | undefined; onClose: () => void }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { changed } = useCalendar();
  const nameId = useId();
  const creating = calendar === undefined;
  const renamable = creating || isExtraCalendar(calendar);
  const [name, setName] = useState(calendar?.name ?? '');
  const [color, setColor] = useState<Color>(calendar?.color ?? 'teal');
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (renamable && trimmed === '') return setMissing(true);
    setBusy(true);
    try {
      if (creating) {
        const created = await api.createOwnCalendar(trimmed);
        if (created.color !== color) await api.updateCalendar(created.id, { color });
        toast.success(t('webmail.calendar.list.createdToast', { name: trimmed }));
      } else {
        await api.updateCalendar(calendar.id, { color, ...(renamable && trimmed !== calendar.name ? { name: trimmed } : {}) });
      }
      changed();
      onClose();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{creating ? t('webmail.calendar.list.newTitle') : renamable ? t('webmail.calendar.list.edit') : t('webmail.calendar.list.colorOf', { name: calendarName(calendar, t) })}</DialogTitle>
          <DialogDescription>{t(creating || renamable ? 'webmail.calendar.list.newDescription' : 'webmail.calendar.list.colorDescription')}</DialogDescription>
        </DialogHeader>
        <form id={`${nameId}-form`} className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          {renamable && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={nameId}>{t('webmail.calendar.list.name')}</Label>
              <Input
                id={nameId}
                autoFocus
                value={name}
                maxLength={256}
                aria-invalid={missing}
                onChange={(event) => {
                  setName(event.target.value);
                  setMissing(false);
                }}
              />
              {missing && (
                <p role="alert" className="text-sm text-destructive">
                  {t('webmail.calendar.list.nameRequired')}
                </p>
              )}
            </div>
          )}
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-medium">{t('webmail.calendar.list.colorLabel')}</legend>
            <ColorChoice label={t('webmail.calendar.list.colorLabel')} value={color} onChange={(next) => next && setColor(next)} />
          </fieldset>
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={`${nameId}-form`} disabled={busy}>
            {creating ? t('webmail.calendar.list.create') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
